/** The drawers behind Needs-you items (screens §F1): surprise, request, held narrative, coach signals, pre-check catch-up. */
import { useEffect, useState } from "react";
import type { BoardTeamRow, CoachTurnView, NarrativeReviewItem, RosterMember, RoundSummary } from "@bsim/contracts";
import { Drawer } from "../theme/Drawer";
import { GxPill } from "../theme/GxPill";
import { EventPreviewList, type Preview } from "./EventPreview";
import { MORE_TIME_KINDS, REQUEST_KINDS } from "./NeedsYou";
import { roundInPlay } from "./derive";
import { GxLoading } from "../theme/GxLoading";
import type { QueueItem } from "./queue";
import type { Act } from "./useAct";

interface Ctx { readonly cohortId: string; readonly act: Act; readonly onClose: () => void }
type Item<K extends QueueItem["kind"]> = Extract<QueueItem, { kind: K }>;

export function SurpriseDrawer({ cohortId, round, item, teams, act, onClose, onSwap, onDrop }: Ctx & {
  readonly round: RoundSummary; readonly item: Item<"surprise">; readonly teams: readonly BoardTeamRow[]; readonly onSwap: () => void; readonly onDrop: () => void;
}) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const { surprise } = item;
  useEffect(() => {
    let live = true;
    void act("eventInjectionPreview", { cohortId, roundId: round.id }, {
      eventId: surprise.eventId, variantId: surprise.variantId, targetTeamIds: [...surprise.teamIds], timing: { phase: "midRound", month: surprise.month }, bypassTrigger: false,
    }).then((res) => { if (live && res.ok) setPreview(res.value); });
    return () => { live = false; };
  // The preview pins what is held; it re-runs only when the held card changes.
  }, [surprise.eventId, surprise.variantId, surprise.month]);
  return (
    <Drawer title="Surprise held" onClose={onClose} footer={round.state === "awaitingSurprise" ? "Teams see it when you release it from the band." : "Teams never see it before you close the round. You release it from the band."}>
      <div data-testid="drawer.surprise">
        {preview === null ? <GxLoading variant="inline" words="Loading the card…" /> : <EventPreviewList preview={preview} teams={teams} />}
        <div className="gx-fac-qa"><button type="button" className="gx-btn gx-btn--secondary" onClick={onSwap}>Swap card</button><button type="button" className="gx-btn gx-btn--danger" onClick={onDrop}>Drop</button></div>
      </div>
    </Drawer>
  );
}

export function RequestDrawer({ cohortId, item, band, act, onClose }: Ctx & { readonly item: Item<"request">; readonly band: RoundSummary | null }) {
  const { exception } = item;
  const [note, setNote] = useState("");
  const [approved, setApproved] = useState(false);
  const decide = async (decision: "approved" | "denied") => {
    const res = await act("exceptionDecide", { cohortId, exceptionId: exception.id }, { decision, note });
    if (!res.ok) return;
    if (decision === "approved" && MORE_TIME_KINDS.has(exception.kind) && band !== null) setApproved(true);
    else onClose();
  };
  const extend = async () => {
    if (band !== null && (await act("roundExtend", { cohortId, roundId: band.id }, { expectedStateVersion: band.stateVersion, addMinutes: 5 })).ok) onClose();
  };
  return (
    <Drawer title={`Request · ${REQUEST_KINDS[exception.kind] ?? exception.kind}`} onClose={onClose}>
      <div className="gx-fac-form" data-testid="drawer.request">
        <p><b>{item.teamName}</b></p>
        <p className="gx-muted">"{exception.justification}"</p>
        <label className="gx-fac-field"><span>Note to the team</span><textarea className="gx-fac-input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} /></label>
        {approved ? (
          <div className="gx-fac-qa"><button type="button" className="gx-btn gx-btn--accent" data-testid="request.extend" onClick={() => void extend()}>Extend countdown +5</button><button type="button" className="gx-btn gx-btn--ghost" onClick={onClose}>Not now</button></div>
        ) : (
          <div className="gx-fac-qa"><button type="button" className="gx-btn gx-btn--primary" disabled={note.trim() === ""} onClick={() => void decide("approved")}>Approve</button><button type="button" className="gx-btn gx-btn--secondary" disabled={note.trim() === ""} onClick={() => void decide("denied")}>Decline</button></div>
        )}
      </div>
    </Drawer>
  );
}

function NarrativeRow({ n, cohortId, act }: { readonly n: NarrativeReviewItem; readonly cohortId: string; readonly act: Act }) {
  const [reason, setReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const params = { cohortId, generationId: n.generationId };
  return (
    <article className="gx-fac-preview" data-testid={`narrative.${n.generationId}`}>
      <div className="gx-fac-preview-h"><b>{n.narrative.headline}</b><GxPill tone={n.verifier.verdict === "fail" ? "crit" : n.verifier.verdict === "repaired" ? "warn" : "good"}>Verifier · {n.verifier.verdict}</GxPill></div>
      <p className="gx-small gx-muted gx-fac-body">{n.narrative.bodyMarkdown}</p>
      {n.verifier.issues.map((i) => <div key={i.code} className="gx-small gx-faint">{i.code}: {i.message}</div>)}
      {rejecting ? (
        <div className="gx-fac-form">
          <input className="gx-fac-input" aria-label="Reason for rejecting" placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          <div className="gx-fac-qa">
            <button type="button" className="gx-btn gx-btn--secondary" disabled={reason.trim() === ""} onClick={() => void act("narrativeReject", params, { reason, then: "regenerate" })}>Reject and regenerate</button>
            <button type="button" className="gx-btn gx-btn--secondary" disabled={reason.trim() === ""} onClick={() => void act("narrativeReject", params, { reason, then: "fallback_template" })}>Reject, use the template</button>
          </div>
        </div>
      ) : (
        <div className="gx-fac-qa"><button type="button" className="gx-btn gx-btn--primary" onClick={() => void act("narrativeApprove", params, {})}>Approve as is</button><button type="button" className="gx-btn gx-btn--secondary" onClick={() => setRejecting(true)}>Reject…</button></div>
      )}
    </article>
  );
}

export function NarrativeDrawer({ cohortId, item, act, onClose }: Ctx & { readonly item: Item<"narrative"> }) {
  return (
    <Drawer title="Held narratives" onClose={onClose} footer="No editing: approve as is, or reject. Supervisory letters are templates.">
      <div data-testid="drawer.narrative">
        {item.items.length === 0 ? <GxLoading variant="inline" words="Loading the drafts…" /> : item.items.map((n) => <NarrativeRow key={n.generationId} n={n} cohortId={cohortId} act={act} />)}
      </div>
    </Drawer>
  );
}

export function CoachSignalsDrawer({ item, teams, members, onClose }: { readonly item: Item<"coach">; readonly teams: readonly BoardTeamRow[]; readonly members: readonly RosterMember[]; readonly onClose: () => void }) {
  const [names, setNames] = useState(false);
  const who = (t: CoachTurnView) => (names ? (members.find((m) => m.userId === t.userId)?.displayName ?? "A teammate") : "A teammate");
  return (
    <Drawer title="Coach signals" onClose={onClose} footer="Read-only. Names are hidden unless you show them.">
      <div data-testid="drawer.coach">
        <label className="gx-fac-chk"><input type="checkbox" checked={names} onChange={(e) => setNames(e.target.checked)} />Show names</label>
        {item.turns.map((t) => (
          <article key={t.generationId} className="gx-fac-preview">
            <div className="gx-fac-preview-h"><b>{teams.find((x) => x.teamId === t.teamId)?.name ?? "Team"}</b><span className="gx-small gx-faint">{who(t)} · hint {t.hintLevel}</span></div>
            <p className="gx-small">{t.userText}</p>
            <p className="gx-small gx-muted">{t.assistantText}</p>
            <div className="gx-fac-flags">{t.flags.map((f) => <GxPill key={f} tone="warn">{f}</GxPill>)}</div>
          </article>
        ))}
      </div>
    </Drawer>
  );
}

const PRE_WORDS = { not_started: "Not started", in_progress: "In progress", submitted: "Submitted" } as const;

/** Why the pre-check waits, by what the round is doing: played (open, paused, a live surprise), closed and not yet run (the year still to run), or, between rounds, nothing waits and the people who reached it during a round can be called now. */
export function precheckNote(round: RoundSummary | null, deferred: boolean): string {
  if (!roundInPlay(round)) return "No round is being played, so they can be called to the pre-check now.";
  const closed = round?.state === "locked";
  if (!deferred) return closed ? "The round is closed and the year has not been run yet, so the pre-check waits until it has." : "A round is being played, so the pre-check waits until the round ends and they carry on to play first.";
  return closed ? "Deferred means they reached the pre-check while a round was being played, so it waits until the year has been run." : "Deferred means they reached the pre-check while a round was being played, so it waits until the round ends and they carry on to play.";
}

export function PrecheckDrawer({ item, cohortId, round, act, onClose, calledIn = [], onCalled = () => undefined }: { readonly item: Item<"precheck">; readonly cohortId: string; readonly round: RoundSummary | null; readonly act: Act; readonly onClose: () => void; readonly calledIn?: readonly string[]; /** Who the api called in, kept by the board so the queue counts them. */ readonly onCalled?: (userIds: readonly string[]) => void }) {
  // Who the api says it called in (it skips anyone who already submitted); the rows read "Called in" from then on.
  const [sent, setCalled] = useState<readonly string[] | null>(null);
  const called = sent === null && calledIn.length === 0 ? null : [...new Set([...(sent ?? []), ...calledIn])];
  const send = async () => {
    const result = await act("assessmentCatchUp", { cohortId }, { userIds: item.members.map((m) => m.userId) }, { idempotent: true });
    if (result.ok) { setCalled(result.value.started); onCalled(result.value.started); }
  };
  return (
    <Drawer title="Pre-check" onClose={onClose} footer="Players see a prompt on their screen.">
      {item.deferredIds.length === 0 && !roundInPlay(round) ? null : <p className="gx-small gx-muted" data-testid="precheck.deferredNote">{precheckNote(round, item.deferredIds.length > 0)} They are not missing; call them to the pre-check when the room is ready.</p>}
      <ul className="gx-fac-list" data-testid="drawer.precheck">
        {item.members.map((m) => <li key={m.userId}><span>{m.displayName}</span>{called?.includes(m.userId) === true ? <GxPill tone="info">Called in</GxPill> : item.deferredIds.includes(m.userId) && roundInPlay(round) ? <GxPill tone="gold">Deferred: reached it during a round</GxPill> : null}<GxPill tone={m.pre === "in_progress" ? "info" : "neutral"}>{PRE_WORDS[m.pre]}</GxPill></li>)}
      </ul>
      {called === null ? null : <p className="gx-small" role="status" data-testid="precheck.sent">Sent to {called.length} {called.length === 1 ? "person" : "people"}. They see a prompt on their screen.</p>}
      <button type="button" className="gx-btn gx-btn--secondary" data-testid="precheck.send" onClick={() => void send()}>{called === null ? "Call them to the pre-check" : "Call them again"}</button>
    </Drawer>
  );
}
